import { Link } from "react-router-dom";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Check, Sparkles, ArrowRight, Calendar } from "lucide-react";

const ORDER = ["basic", "pro", "pro_unlimited", "consulting"];

export default function PricingSection({ compact = false, currentTier = null, onPick = null }) {
  const { plans } = useSubscription();
  const entries = ORDER.map((k) => plans[k]).filter(Boolean);
  if (!entries.length) return null;

  return (
    <section data-testid="pricing-section" className={compact ? "" : "max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20"}>
      {!compact && (
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-12">
          <div className="dms-label mb-3 justify-center">Pricing</div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342]">Four ways to work with DMS.</h2>
          <p className="mt-4 text-[#475569]">7-day free trial on Creator, Pro and Pro Unlimited. Consulting is one-time and includes a month of Pro.</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 max-w-7xl mx-auto">
        {entries.map((p) => {
          const isFeatured = p.tier === "pro";
          const isUnlimited = p.tier === "pro_unlimited";
          const isConsulting = p.tier === "consulting";
          const isCurrent = currentTier === p.tier;
          return (
            <div
              key={p.tier}
              data-testid={`pricing-card-${p.tier}`}
              className={`dms-card p-6 sm:p-7 relative flex flex-col ${isFeatured ? "ring-2 ring-[#4FD1C5] shadow-[0_12px_36px_rgba(79,209,197,0.18)]" : ""}`}
            >
              {isFeatured && (
                <div className="absolute -top-3 left-6 dms-badge bg-[#0A2342] text-[#4FD1C5]">
                  <Sparkles className="w-3 h-3" /> Most popular
                </div>
              )}
              {isUnlimited && (
                <div className="absolute -top-3 left-6 dms-badge bg-[#4FD1C5] text-[#0A2342]">
                  <Sparkles className="w-3 h-3" /> Unlimited
                </div>
              )}
              {isConsulting && (
                <div className="absolute -top-3 left-6 dms-badge bg-[#1D4E89] text-white">
                  <Calendar className="w-3 h-3" /> 1:1 with Robin
                </div>
              )}
              <h3 className="font-heading font-bold text-xl sm:text-[22px] text-[#0A2342]">{p.name}</h3>
              <p className="mt-1.5 text-[13px] text-[#64748B] leading-relaxed">{p.tagline}</p>
              <div className="mt-5 flex items-baseline gap-1.5">
                <span className="font-heading font-bold text-3xl sm:text-[38px] text-[#0A2342]">&euro;{p.amount.toFixed(2)}</span>
                <span className="text-[#64748B] text-sm">{p.is_one_time ? "one-time" : "/ month"}</span>
              </div>
              {p.monthly_credits === null && !p.is_one_time && (
                <div className="mt-1 text-[12.5px] font-semibold text-[#0F766E]">Unlimited Strategy Credits</div>
              )}
              {typeof p.monthly_credits === "number" && (
                <div className="mt-1 text-[12.5px] font-semibold text-[#1D4E89]">{p.monthly_credits} Strategy Credits / month</div>
              )}
              <ul className="mt-5 space-y-2 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[13.5px] text-[#1F2937]">
                    <Check className="w-4 h-4 mt-0.5 text-[#4FD1C5] shrink-0" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                {isCurrent ? (
                  <div data-testid={`pricing-current-${p.tier}`} className="w-full text-center py-2.5 rounded-md bg-[#F1F5F9] text-[#64748B] font-semibold text-sm">
                    Your current plan
                  </div>
                ) : onPick ? (
                  <button
                    data-testid={`pricing-cta-${p.tier}`}
                    onClick={() => onPick(p.tier)}
                    className={isFeatured ? "w-full dms-btn-accent" : isConsulting ? "w-full dms-btn-primary" : "w-full dms-btn-ghost"}
                  >
                    {isConsulting ? "Book consultation" : "Start 7-day trial"} <ArrowRight className="w-4 h-4" />
                  </button>
                ) : isConsulting ? (
                  <Link to="/consulting" data-testid={`pricing-cta-${p.tier}`} className="w-full dms-btn-primary">
                    Book consultation <ArrowRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <Link to="/register" data-testid={`pricing-cta-${p.tier}`} className={isFeatured ? "w-full dms-btn-accent" : "w-full dms-btn-ghost"}>
                    Start 7-day trial <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
